/**
 * Inventory Count Pivot — Model × Package × Colour × Branch.
 *
 * Renders one row per (Model, Package, Colour) with a column per active
 * branch (only branches that actually hold stock in the filter are shown)
 * plus a Total column and footer sums. Branch-locked users only see their
 * own branch; admins see all.
 *
 * Filters: vehicle status (default `available`), sub-dealer inclusion,
 * and a simple text search that filters the visible rows client-side.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CaretLeft, Download } from "@phosphor-icons/react";

import PageHeader from "@/components/PageHeader"; // eslint-disable-line no-unused-vars
import { api, formatApiError } from "@/lib/api";
import useBranchTypes from "@/hooks/useBranchTypes";
import { MODEL_CATEGORY_OPTIONS } from "@/components/PackageFormDrawer";

const STATUS_OPTIONS = [
  { value: "available", label: "Available" },
  { value: "available,reserved", label: "Available + Reserved" },
  { value: "reserved", label: "Reserved only" },
  { value: "sold", label: "Sold" },
  { value: "all", label: "All statuses" },
];

// Subtle per-model row tints so a long pivot is scannable at a glance.
// Kept intentionally light so numbers stay legible on top.
const MODEL_ROW_TINTS = {
  ELECTRIC: "bg-emerald-50 hover:bg-emerald-100",
  MOPED: "bg-amber-50 hover:bg-amber-100",
  MOTORCYCLE: "bg-sky-50 hover:bg-sky-100",
  SCOOTER: "bg-violet-50 hover:bg-violet-100",
  PREMIUM: "bg-rose-50 hover:bg-rose-100",
  "SUPER PREMIUM": "bg-red-100 hover:bg-red-200",
  COMMERCIAL: "bg-orange-50 hover:bg-orange-100",
  OTHER: "bg-neutral-100 hover:bg-neutral-200",
};
const MODEL_BADGE_TINTS = {
  ELECTRIC: "bg-emerald-200 text-emerald-900",
  MOPED: "bg-amber-200 text-amber-900",
  MOTORCYCLE: "bg-sky-200 text-sky-900",
  SCOOTER: "bg-violet-200 text-violet-900",
  PREMIUM: "bg-rose-200 text-rose-900",
  "SUPER PREMIUM": "bg-red-200 text-red-950",
  COMMERCIAL: "bg-orange-200 text-orange-900",
  OTHER: "bg-neutral-200 text-neutral-900",
};

export default function InventoryCountPivotPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("available");
  const [includeSubDealer, setIncludeSubDealer] = useState(false);
  const [search, setSearch] = useState("");
  // Filters mirroring the ReportsPage FilterBar: branch type → branch,
  // then model category. Branch options are re-fetched when branch
  // type changes so users can't pick a branch outside the selected
  // type. Empty string on any filter means "all".
  const [branchTypeId, setBranchTypeId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [modelCategory, setModelCategory] = useState("");
  const [packageId, setPackageId] = useState("");
  const [dealerCode, setDealerCode] = useState("");
  const [year, setYear] = useState("");
  const { types: branchTypes } = useBranchTypes();
  const [branches, setBranches] = useState([]);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const params = {};
    if (branchTypeId) params.branch_type_id = branchTypeId;
    api.get("/branches", { params })
      .then((r) => { if (!cancelled) setBranches(r.data || []); })
      .catch(() => { if (!cancelled) setBranches([]); });
    return () => { cancelled = true; };
  }, [branchTypeId]);

  // If the selected branch is no longer in the visible list after a
  // branch-type change, clear it so the query doesn't ship a stale id.
  useEffect(() => {
    if (branchId && branches.length && !branches.some((b) => b.id === branchId)) {
      setBranchId("");
    }
  }, [branches, branchId]);

  // Load active packages once. Filtering by model happens client-side
  // so switching Model doesn't refetch the whole list.
  useEffect(() => {
    let cancelled = false;
    api.get("/packages", { params: { active: true } })
      .then((r) => { if (!cancelled) setPackages(r.data || []); })
      .catch(() => { if (!cancelled) setPackages([]); });
    return () => { cancelled = true; };
  }, []);

  // Narrow package dropdown to the selected model category. Also
  // auto-clears any stale package pick that's outside the model.
  const packagesForModel = useMemo(() => {
    const src = packages || [];
    const scoped = !modelCategory
      ? src
      : src.filter((p) => (p.model_category || "").toUpperCase() === modelCategory.toUpperCase());
    // The package master carries one row per (name, branch_type), so
    // the same package name can appear multiple times. Deduplicate for
    // the dropdown — the backend expands the picked id to all siblings.
    const seen = new Set();
    const deduped = [];
    for (const p of scoped) {
      if (seen.has(p.name)) continue;
      seen.add(p.name);
      deduped.push(p);
    }
    deduped.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return deduped;
  }, [packages, modelCategory]);

  useEffect(() => {
    if (packageId && !packagesForModel.some((p) => p.id === packageId)) {
      setPackageId("");
    }
  }, [packagesForModel, packageId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { status, include_sub_dealer: includeSubDealer };
    if (branchTypeId) params.branch_type_id = branchTypeId;
    if (branchId) params.branch_id = branchId;
    if (modelCategory) params.model_category = modelCategory;
    if (packageId) params.package_id = packageId;
    if (dealerCode) params.dealer_code = dealerCode;
    if (year) params.year = year;
    api.get("/reports/inventory-count-pivot", { params })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) toast.error(formatApiError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [status, includeSubDealer, branchTypeId, branchId, modelCategory, packageId, dealerCode, year]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter((r) =>
      [r.model, r.package, r.colour].some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [data, search]);

  const pivotBranches = data?.branches || [];

  const exportCsv = () => {
    if (!data) return;
    const header = ["Model", "Package", "Colour", ...pivotBranches.map((b) => b.name), "Total"];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [header.map(escape).join(",")];
    filtered.forEach((r) => {
      const cells = [r.model, r.package, r.colour, ...pivotBranches.map((b) => r.by_branch[b.id] || 0), r.total];
      lines.push(cells.map(escape).join(","));
    });
    // Grand-total footer
    const totalRow = ["TOTAL", "", "", ...pivotBranches.map((b) => data.totals_by_branch[b.id] || 0), data.grand_total];
    lines.push(totalRow.map(escape).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inventory-pivot-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-2 p-4">
      <div className="flex items-center justify-between">
        <Link to="/reports" className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-ink-muted hover:text-ink">
          <CaretLeft size={12} weight="bold" /> Reports
        </Link>
      </div>
      <div className="flex items-baseline gap-3">
        <h1 className="text-xl font-bold">Inventory Count Pivot</h1>
        <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Model × Package × Colour · by branch</span>
      </div>

      {/* Filter row: Branch Type → Branch → Model (matches ReportsPage FilterBar) */}
      <div className="flex items-end gap-3 flex-wrap border border-canvas-line bg-canvas-panel/40 px-3 py-2">
        <label className="flex flex-col gap-0.5">
          <span className="text-[9px] uppercase tracking-widest text-ink-muted font-mono">Branch Type</span>
          <select
            value={branchTypeId}
            onChange={(e) => setBranchTypeId(e.target.value)}
            data-testid="pivot-branch-type"
            className="px-2 py-1 bg-canvas border border-canvas-line text-xs min-w-[9rem]"
          >
            <option value="">All types</option>
            {branchTypes.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[9px] uppercase tracking-widest text-ink-muted font-mono">Branch</span>
          <select
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            data-testid="pivot-branch"
            className="px-2 py-1 bg-canvas border border-canvas-line text-xs min-w-[11rem]"
          >
            <option value="">All branches</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[9px] uppercase tracking-widest text-ink-muted font-mono">Model</span>
          <select
            value={modelCategory}
            onChange={(e) => setModelCategory(e.target.value)}
            data-testid="pivot-model"
            className="px-2 py-1 bg-canvas border border-canvas-line text-xs min-w-[9rem]"
          >
            <option value="">All models</option>
            {MODEL_CATEGORY_OPTIONS.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[9px] uppercase tracking-widest text-ink-muted font-mono">
            Package{modelCategory ? ` · ${modelCategory.toLowerCase()}` : ""}
          </span>
          <select
            value={packageId}
            onChange={(e) => setPackageId(e.target.value)}
            data-testid="pivot-package"
            className="px-2 py-1 bg-canvas border border-canvas-line text-xs min-w-[12rem]"
          >
            <option value="">All packages ({packagesForModel.length})</option>
            {packagesForModel.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[9px] uppercase tracking-widest text-ink-muted font-mono">DMS Code</span>
          <select
            value={dealerCode}
            onChange={(e) => setDealerCode(e.target.value)}
            data-testid="pivot-dealer-code"
            className="px-2 py-1 bg-canvas border border-canvas-line text-xs min-w-[7rem]"
          >
            <option value="">All codes</option>
            {(data?.dealer_code_options || []).map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[9px] uppercase tracking-widest text-ink-muted font-mono">Year of Mfg</span>
          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            data-testid="pivot-year"
            className="px-2 py-1 bg-canvas border border-canvas-line text-xs min-w-[6rem]"
          >
            <option value="">All years</option>
            {(data?.year_options || []).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[9px] uppercase tracking-widest text-ink-muted font-mono">Status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="pivot-status"
            className="px-2 py-1 bg-canvas border border-canvas-line text-xs min-w-[9rem]"
          >
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs pb-1">
          <input
            type="checkbox"
            checked={includeSubDealer}
            onChange={(e) => setIncludeSubDealer(e.target.checked)}
            data-testid="pivot-include-sd"
          />
          <span>Include sub-dealer stock</span>
        </label>
        <div className="ml-auto flex items-center gap-2 pb-1">
          <input
            placeholder="Filter model, package, colour…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="pivot-search"
            className="px-2 py-1 bg-canvas border border-canvas-line text-xs w-56 focus:outline-none focus:border-ink"
          />
          <button
            onClick={exportCsv}
            disabled={!data || filtered.length === 0}
            data-testid="pivot-csv"
            className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider hover:border-ink disabled:opacity-40 flex items-center gap-1"
          >
            <Download size={11} weight="bold" /> CSV
          </button>
        </div>
      </div>

      {/* Summary tiles — compact strip so the pivot table has room */}
      {data && (
        <div className="grid grid-cols-4 gap-2">
          <SummaryTile label="Total Vehicles" value={data.grand_total} />
          <SummaryTile label="Unique Rows" value={filtered.length} />
          <SummaryTile label="Branches" value={pivotBranches.length} />
          <SummaryTile label="Filter" value={search ? `"${search}"` : "—"} />
        </div>
      )}

      {/* Pivot table — everything below is intentionally dense so a
          20-branch × 80-row snapshot fits on a single laptop screen.
          Branch columns are fixed-width narrow so 15+ branches fit,
          numbers use a larger font so counts are readable at a glance. */}
      <div className="border border-canvas-line overflow-x-auto inline-block max-w-full align-top">
        <table className="text-[13px] border-collapse" data-testid="pivot-table" style={{ width: "max-content" }}>
          <thead className="bg-canvas-panel border-b border-canvas-line sticky top-0 z-10">
            <tr>
              <th className="px-2 py-2 font-mono text-[12px] font-bold uppercase text-left whitespace-nowrap" style={{ width: "1%" }}>Model</th>
              <th className="px-2 py-2 font-mono text-[12px] font-bold uppercase text-left whitespace-nowrap" style={{ width: "1%" }}>Package</th>
              <th className="px-2 py-2 font-mono text-[12px] font-bold uppercase text-left whitespace-nowrap" style={{ width: "1%" }}>Colour</th>
              {pivotBranches.map((b) => (
                <th
                  key={b.id}
                  className="px-0 py-1 font-mono text-[12px] font-bold uppercase align-bottom text-center border-l border-canvas-line/40"
                  title={b.name}
                  style={{ width: "2.5rem", minWidth: "2.5rem", maxWidth: "2.5rem" }}
                >
                  <div
                    className="mx-auto whitespace-nowrap"
                    style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", minHeight: "6rem" }}
                  >
                    {b.name}
                  </div>
                </th>
              ))}
              <th
                className="px-2 py-2 font-mono text-[12px] font-bold uppercase text-right whitespace-nowrap bg-canvas-line/60"
                style={{ width: "3.5rem", minWidth: "3.5rem" }}
              >
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4 + pivotBranches.length} className="px-3 py-6 text-center text-ink-muted text-xs">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={4 + pivotBranches.length} className="px-3 py-6 text-center text-ink-muted text-xs">No inventory matches the current filters.</td></tr>
            )}
            {filtered.map((r, idx) => {
              const modelKey = (r.model || "OTHER").toUpperCase();
              const rowTint = MODEL_ROW_TINTS[modelKey] || MODEL_ROW_TINTS.OTHER;
              const badgeTint = MODEL_BADGE_TINTS[modelKey] || MODEL_BADGE_TINTS.OTHER;
              return (
                <tr
                  key={`${r.model}-${r.package}-${r.colour}-${idx}`}
                  className={`border-b border-canvas-line/40 ${rowTint}`}
                  data-testid={`pivot-row-${idx}`}
                >
                  <td className="px-2 py-1 whitespace-nowrap">
                    <span className={`inline-block px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${badgeTint}`}>
                      {r.model}
                    </span>
                  </td>
                  <td className="px-2 py-1 whitespace-nowrap">{r.package}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{r.colour}</td>
                  {pivotBranches.map((b) => (
                    <td
                      key={b.id}
                      className={`px-0.5 py-1 text-center font-mono font-semibold text-[14px] border-l border-canvas-line/30 ${
                        r.by_branch[b.id] ? "" : "text-ink-subtle/40"
                      }`}
                      style={{ width: "2.5rem", minWidth: "2.5rem", maxWidth: "2.5rem" }}
                    >
                      {r.by_branch[b.id] || ""}
                    </td>
                  ))}
                  <td
                    className="px-2 py-1 text-right font-mono font-bold text-[14px] bg-black/5"
                    style={{ width: "3.5rem", minWidth: "3.5rem" }}
                  >
                    {r.total}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {data && filtered.length > 0 && (
            <tfoot className="bg-canvas-panel border-t border-canvas-line sticky bottom-0">
              <tr>
                <td colSpan={3} className="px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">Total per branch</td>
                {pivotBranches.map((b) => (
                  <td
                    key={b.id}
                    className="px-0.5 py-1 text-center font-mono font-bold text-[14px] border-l border-canvas-line/40"
                    style={{ width: "2.5rem", minWidth: "2.5rem", maxWidth: "2.5rem" }}
                  >
                    {data.totals_by_branch[b.id] || 0}
                  </td>
                ))}
                <td
                  className="px-2 py-1 text-right font-mono font-bold text-base bg-canvas-line/40"
                  data-testid="pivot-grand-total"
                  style={{ width: "3.5rem", minWidth: "3.5rem" }}
                >
                  {data.grand_total}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div className="border border-canvas-line bg-canvas px-3 py-1.5">
      <div className="text-[9px] uppercase tracking-widest text-ink-muted font-mono">{label}</div>
      <div className="text-lg font-bold mt-0.5 font-mono truncate">{value}</div>
    </div>
  );
}
